import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../../components/admin/Layout';
import axios from '../../../utils/axios';
import { BiDish, BiImage, BiTrash } from 'react-icons/bi';
import { useTranslation } from '../../../context/LanguageContext';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import ToggleSwitch from '../../../components/common/ToggleSwitch';

const EditItem = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t, isRTL } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [errors, setErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        price: '',
        category_id: '',
        is_active: true,
        sort_order: '0',
        image: null,
        color: ''
    });

    const loadData = useCallback(async () => {
        if (!id) {
            console.error('No ID provided');
            toast.error(t('admin.errorLoadingData'));
            navigate('/admin/items');
            return;
        }

        try {

            
            const [categoriesResponse, itemResponse] = await Promise.all([
                axios.get('/categories'),
                axios.get(`/items/${id}`, {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                })
            ]);



            setCategories(categoriesResponse.data.data);

            // Access the nested item data
            const item = itemResponse.data.data;


            if (item) {

                setFormData({
                    name: item.name || '',
                    name_ar: item.name_ar || '',
                    description: item.description || '',
                    description_ar: item.description_ar || '',
                    price: item.price?.toString() || '',
                    category_id: item.category_id?.toString() || '',
                    is_active: item.is_active === 1 || item.is_active === true,
                    sort_order: item.sort_order?.toString() || '0',
                    image: null,
                    color: item.color || ''
                });

                if (item.image) {
                    setImagePreview(item.image);
                }
            } else {
                console.error('Invalid item data received:', itemResponse.data);
                toast.error(t('admin.errorLoadingData'));
                navigate('/admin/items');
            }
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error(t('admin.errorLoadingData'));
            navigate('/admin/items');
        } finally {
            setInitialLoading(false);
        }
    }, [id, navigate, t]);

    useEffect(() => {

        loadData();
    }, [loadData, id]);

    const handleInputChange = (e) => {
        // Clear error for the field being changed
        if (errors[e.target.name]) {
            setErrors(prev => ({ ...prev, [e.target.name]: null }));
        }
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, image: file }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const removeImage = () => {
        setImagePreview(null);
        setFormData(prev => ({ 
            ...prev, 
            image: null,
            remove_image: true // Add flag to indicate image should be deleted
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const form = new FormData();
            
            // Handle is_active separately to ensure it's 0 or 1
            form.append('is_active', formData.is_active ? '1' : '0');
            
            // Handle image removal
            if (formData.remove_image) {
                form.append('remove_image', '1');
            }

            // Append other form fields
            Object.keys(formData).forEach(key => {
                // Skip special handling fields
                if (key === 'is_active' || key === 'remove_image') {
                    return;
                }
                // Skip image field if we're removing the image
                if (key === 'image' && formData.remove_image) {
                    return;
                }
                // Only append non-null values
                if (formData[key] !== null) {
                    form.append(key, formData[key]);
                }
            });

            form.append('_method', 'PUT');



            const response = await axios.post(`/items/${id}`, form, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json'
                }
            });



            // Check if we have data in the response
            if (response.data && response.data.data) {
                toast.success(t('admin.successSave'));
                navigate('/admin/items');
            } else {

                toast.error(t('admin.errorUpdatingItem'));
            }
        } catch (error) {
            console.error('Error updating item:', error);
            
            // Show validation errors if available
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                errorMessages.forEach(message => toast.error(message));
            } else {
                toast.error(t('admin.errorUpdatingItem'));
            }
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-[calc(100vh-4rem)]">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={`container mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
                <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <div className={`flex items-center justify-between mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div>
                            <h1 className={`text-3xl font-bold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('admin.editItem')}
                            </h1>
                            <p className={`mt-1 text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('admin.editItemDescription')}
                            </p>
                        </div>
                        <BiDish className="h-8 w-8 text-yellow-500" />
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100">
                        <div className="p-6 space-y-6">
                            {/* Image Upload Section */}
                            <div className="space-y-2">
                                <label className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {t('admin.itemImage')}
                                </label>
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                                    <div className="space-y-1 text-center">
                                        {imagePreview ? (
                                            <div className="relative inline-block">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="mx-auto h-32 w-32 object-cover rounded-lg"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={removeImage}
                                                    className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200 transition-colors duration-200"
                                                >
                                                    <BiTrash className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <BiImage className="mx-auto h-12 w-12 text-gray-400" />
                                                <div className="flex justify-center text-sm text-gray-600">
                                                    <label
                                                        htmlFor="image-upload"
                                                        className="relative cursor-pointer rounded-md font-medium text-yellow-600 hover:text-yellow-500 focus-within:outline-none"
                                                    >
                                                        <span>{t('admin.uploadImage')}</span>
                                                        <input
                                                            id="image-upload"
                                                            name="image"
                                                            type="file"
                                                            accept="image/*"
                                                            className="sr-only"
                                                            onChange={handleImageChange}
                                                        />
                                                    </label>
                                                </div>
                                                <p className="text-xs text-gray-500">{t('admin.imageRequirements')}</p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Name Fields */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="name" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('admin.nameEnglish')}
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="name_ar" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('admin.nameArabic')}
                                    </label>
                                    <input
                                        type="text"
                                        name="name_ar"
                                        id="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleInputChange}
                                        required
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                        dir="rtl"
                                    />
                                </div>
                            </div>

                            {/* Description Fields */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="description" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('admin.descriptionEnglish')}
                                    </label>
                                    <textarea
                                        name="description"
                                        id="description"
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="description_ar" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('admin.descriptionArabic')}
                                    </label>
                                    <textarea
                                        name="description_ar"
                                        id="description_ar"
                                        rows={3}
                                        value={formData.description_ar}
                                        onChange={handleInputChange}
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                        dir="rtl"
                                    />
                                </div>
                            </div>

                            {/* Price and Category */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="price" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('admin.price')}
                                    </label>
                                    <input
                                        type="number"
                                        name="price"
                                        id="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        required
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="category_id" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('admin.category')}
                                    </label>
                                    <select
                                        name="category_id"
                                        id="category_id"
                                        value={formData.category_id}
                                        onChange={handleInputChange}
                                        required
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm appearance-none`}
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                                            backgroundPosition: isRTL ? 'left 0.5rem center' : 'right 0.5rem center',
                                            backgroundRepeat: 'no-repeat',
                                            backgroundSize: '1.5em 1.5em',
                                            paddingRight: isRTL ? '1rem' : '2.5rem',
                                            paddingLeft: isRTL ? '2.5rem' : '1rem'
                                        }}
                                    >
                                        <option value="">{t('admin.selectCategory')}</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {isRTL ? category.name_ar : category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Sort Order and Status */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="sort_order" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('admin.sortOrder')}
                                    </label>
                                    <input
                                        type="number"
                                        name="sort_order"
                                        id="sort_order"
                                        value={formData.sort_order}
                                        onChange={handleInputChange}
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    />
                                </div>
                                <div className="flex items-center">
                                    <ToggleSwitch
                                        checked={formData.is_active}
                                        onChange={handleInputChange}
                                        name="is_active"
                                        label={t('admin.status')}
                                    />
                                </div>
                            </div>

                            {/* Color */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mt-6">
                                <div>
                                    <label htmlFor="color" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('admin.color')}
                                    </label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <div className="flex-shrink-0 w-12">
                                            <input
                                                type="color"
                                                id="color"
                                                name="color"
                                                value={formData.color || '#FFFFFF'}
                                                onChange={handleInputChange}
                                                className="block w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            name="color"
                                            value={formData.color || '#FFFFFF'}
                                            onChange={handleInputChange}
                                            placeholder="#FFFFFF"
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                                            pattern="^#[0-9A-Fa-f]{6}$"
                                            maxLength="7"
                                        />
                                        <span className="text-sm text-gray-500">
                                            {formData.color || t('admin.selectColor')}
                                        </span>
                                    </div>
                                    {errors?.color && <p className="mt-1.5 text-sm text-red-500">{errors.color[0]}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => navigate('/admin/items')}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                >
                                    <FiArrowLeft className={isRTL ? 'ml-2' : 'mr-2'} />
                                    {t('admin.cancel')}
                                </button>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`inline-flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg
                                        hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 
                                        focus:ring-offset-2 transform transition-all duration-200
                                        ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md active:scale-95'}`}
                                >
                                    <FiSave className={isRTL ? 'ml-2' : 'mr-2'} />
                                    {loading ? t('admin.saving') : t('admin.save')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default EditItem;
